import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Car,
  Gauge,
  Map as MapIcon,
  Search,
  Star,
  X,
} from 'lucide-react';

import { cn, displayVehicle } from '@/lib/utils';
import { EventLiveDevice } from '../../hooks/useEventLiveDevices';
import { useMediaQuery } from '../../hooks/useMediaQuery';
import { compareCarNumbers, displayCarNumber } from './carNumber';
import FeedPlayer from './FeedPlayer';
import LagChip from './LagChip';
import { formatLapTime, formatSigma, sigmaToneClass } from './LapSelector';
import MapCanvas from './MapCanvas';
import { GpsSummary } from '../../types';
import { SpeedChart } from '../../plugins/synced_viewer/SpeedChart';
import { Scrubber } from './Scrubber';
import { LagInfo, SEVERITY_TEXT } from './lag';
import { deriveMobileContext, MobileContext } from './mobileState';
import SessionSelector from './SessionSelector';
import StageStrip from './StageStrip';
import type { ViewerController } from './ViewerModeRouter';
import './viewer.css';

const ORIENTATION_LANDSCAPE = '(orientation: landscape)';
const G_MPS2 = 9.80665;

const mutedDash = <span className="text-fg-muted">&mdash;</span>;

function driverName(device: EventLiveDevice): string {
  return device.driver_name || device.device_id;
}

function isRecording(c: ViewerController, device: EventLiveDevice | null): boolean {
  return !c.replay && c.lapOverride == null && device?.session_id != null;
}

function displaySpeedKmh(c: ViewerController, device: EventLiveDevice | null): number | null {
  if (c.playbackSpeedKmh != null) return c.playbackSpeedKmh;
  if (!c.replay && device?.speed_mps != null) return device.speed_mps * 3.6;
  return null;
}

const MobileMetric: React.FC<{
  label: string;
  value: React.ReactNode;
  unit?: string;
  tone?: string;
  title?: string;
}> = ({ label, value, unit, tone, title }) => (
  <div className="tv-mobile-metric" title={title}>
    <div className="tv-mobile-metric-label">{label}</div>
    <div className={cn('tv-mobile-metric-value', tone)}>
      {value}
      {unit && <span className="tv-mobile-metric-unit">{unit}</span>}
    </div>
  </div>
);

const MobileSection: React.FC<{
  title: string;
  meta?: React.ReactNode;
  children: React.ReactNode;
}> = ({ title, meta, children }) => (
  <section className="tv-mobile-section">
    <div className="tv-mobile-section-head">
      <span>{title}</span>
      {meta != null && <span>{meta}</span>}
    </div>
    {children}
  </section>
);

const ViewerNavIsland: React.FC<{
  active: MobileContext;
  orientation: 'portrait' | 'landscape';
  hasCar: boolean;
  onChange: (context: MobileContext) => void;
}> = ({ active, orientation, hasCar, onChange }) => {
  const items: Array<{ key: MobileContext; label: string; icon: typeof Gauge; disabled?: boolean }> = [
    { key: 'watch', label: 'Watch', icon: Gauge, disabled: !hasCar },
    { key: 'map', label: 'Map', icon: MapIcon },
    { key: 'cars', label: 'Cars', icon: Car },
  ];
  return (
    <nav
      className={cn(
        'tv-mobile-nav-island',
        orientation === 'landscape' ? 'tv-mobile-nav-landscape' : 'tv-mobile-nav-portrait',
      )}
      aria-label="Viewer sections"
    >
      {items.map(({ key, label, icon: Icon, disabled }) => (
        <button
          key={key}
          type="button"
          disabled={disabled}
          onClick={() => onChange(key)}
          aria-label={label}
          title={disabled ? 'Pick a car first' : label}
          className={cn(
            'tv-mobile-nav-button',
            active === key && 'is-active',
            disabled && 'is-disabled',
          )}
        >
          <Icon className="size-5" />
          {/* Text label under the glyph — Gauge/Map/Car icons alone
              aren't guessable for a first-time fan. */}
          <span className="tv-mobile-nav-label">{label}</span>
        </button>
      ))}
    </nav>
  );
};

const MobileDriverHeader: React.FC<{
  device: EventLiveDevice;
  replay: boolean;
  recording: boolean;
  followed: boolean;
  lag: LagInfo;
  onToggleFollow: (id: string) => void;
  floating?: boolean;
}> = ({ device, replay, recording, followed, lag, onToggleFollow, floating = false }) => (
  <div className={cn('tv-mobile-driver-header', floating && 'tv-mobile-driver-floating')}>
    <div className="tv-mobile-driver-number">{displayCarNumber(device)}</div>
    <div className="min-w-0 flex-1">
      <div className="truncate text-[15px] font-bold leading-tight text-fg">{driverName(device)}</div>
      <div className="mt-px truncate text-[10px] text-fg-muted">
        {/* Separator only when a REAL vehicle exists — "." is the
            operator's intentional-blank convention (displayVehicle),
            and "— / idle" read as a rendering bug. */}
        {displayVehicle(device.vehicle) && (
          <>
            {displayVehicle(device.vehicle)}
            <span className="mx-1.5 text-fg-muted/60">/</span>
          </>
        )}
        {replay ? 'replay' : recording ? 'recording' : 'idle'}
      </div>
    </div>
    <LagChip lag={lag} className="hidden min-[360px]:inline-flex" />
    <button
      type="button"
      onClick={() => onToggleFollow(device.device_id)}
      title={followed ? 'Unfollow this car' : 'Follow this car'}
      className={cn('tv-mobile-follow-button', followed && 'is-followed')}
    >
      <Star className={cn('size-4', followed && 'fill-current')} />
      <span>{followed ? 'Following' : 'Follow'}</span>
    </button>
  </div>
);

const MobileTelemetryOverlay: React.FC<{
  c: ViewerController;
  device: EventLiveDevice;
  collapsed: boolean;
  onCollapsedChange: (collapsed: boolean) => void;
}> = ({ c, device, collapsed, onCollapsedChange }) => {
  const lag = c.playerLagInfo ?? c.lagFor(device);
  const speedKmh = displaySpeedKmh(c, device);
  const gForce =
    device.accel_peak_mps2 != null ? (device.accel_peak_mps2 / G_MPS2).toFixed(1) : null;

  if (collapsed) {
    return (
      <button
        type="button"
        className="tv-mobile-telemetry-collapsed"
        onClick={() => onCollapsedChange(false)}
        aria-label="Show telemetry"
      >
        <Gauge className="size-4" />
      </button>
    );
  }

  return (
    <div className="tv-mobile-telemetry">
      <div>
        <b>{speedKmh != null ? Math.round(speedKmh) : mutedDash}</b>
        <span>km/h</span>
      </div>
      <div className={SEVERITY_TEXT[lag.severity]}>
        {/* 'idle'/'no fix' are STATUS words, not lag values — an idle
            car has no lag to report, so the cell dashes out. */}
        <b>{lag.severity === 'offline' ? mutedDash : lag.label}</b>
        <span>lag</span>
      </div>
      <div>
        <b>{gForce ?? mutedDash}</b>
        <span>G</span>
      </div>
      <div>
        <b>{device.heading_deg != null ? `${Math.round(device.heading_deg)}\u00b0` : mutedDash}</b>
        <span>hdg</span>
      </div>
      <button
        type="button"
        onClick={() => onCollapsedChange(true)}
        aria-label="Collapse telemetry"
      >
        <X className="size-3.5" />
      </button>
    </div>
  );
};

const MobileVideoStage: React.FC<{
  c: ViewerController;
  device: EventLiveDevice | null;
  landscape: boolean;
  telemetryCollapsed: boolean;
  onTelemetryCollapsedChange: (collapsed: boolean) => void;
  onControlsHiddenChange?: (hidden: boolean) => void;
  onMaximize?: () => void;
  maximized?: boolean;
}> = ({
  c,
  device,
  landscape,
  telemetryCollapsed,
  onTelemetryCollapsedChange,
  onControlsHiddenChange,
  onMaximize,
  maximized,
}) => {
  const hasFeed = c.mountedSessionId != null;
  const recording = isRecording(c, device);

  return (
    <section
      className={cn(
        'tv-mobile-video-stage',
        landscape ? 'tv-mobile-video-landscape' : 'tv-mobile-video-portrait',
      )}
    >
      {hasFeed ? (
        <FeedPlayer
          key={c.mountedPlayerKey}
          sessionId={c.mountedSessionId}
          playerRef={c.playerRef}
          onUtcUpdate={c.handlePlayheadUtc}
          onVideoEdgeUpdate={c.onPlayerEdge}
          onPdtReady={c.handlePdtReady}
          initialSeekUtcMs={c.initialSeekUtcMs}
          vod={c.playerVod}
          badge={c.videoBadge}
          fill={landscape}
          heightFit={landscape}
          onControlsHiddenChange={onControlsHiddenChange}
          onMaximize={onMaximize}
          maximized={maximized}
        />
      ) : (
        <div className="tv-mobile-video-empty">
          <Gauge className="size-6 text-accent" />
          <span>{device ? 'waiting for feed' : 'pick a car to watch'}</span>
        </div>
      )}
      {landscape && device && (
        <>
          <MobileDriverHeader
            device={device}
            replay={c.replay}
            recording={recording}
            followed={c.followedIds.has(device.device_id)}
            lag={c.lagFor(device)}
            onToggleFollow={c.toggleFollow}
            floating
          />
          <MobileTelemetryOverlay
            c={c}
            device={device}
            collapsed={telemetryCollapsed}
            onCollapsedChange={onTelemetryCollapsedChange}
          />
        </>
      )}
    </section>
  );
};

const MobileWatchPanel: React.FC<{
  c: ViewerController;
  device: EventLiveDevice | null;
  onClose?: () => void;
}> = ({ c, device, onClose }) => {
  if (!device) {
    return (
      <div className="tv-mobile-empty-panel">
        <Gauge className="size-5 text-accent" />
        <span>Pick a car to open Watch</span>
      </div>
    );
  }

  const recording = isRecording(c, device);
  const lag = c.playerLagInfo ?? c.lagFor(device);
  const speedKmh = displaySpeedKmh(c, device);
  const gForce =
    device.accel_peak_mps2 != null ? (device.accel_peak_mps2 / G_MPS2).toFixed(1) : null;
  const stagePct =
    c.stageProgress && c.stageProgress.pct >= 0
      ? Math.min(100, Math.max(0, c.stageProgress.pct))
      : null;
  const recentLaps = c.laps.map((lap, idx) => ({ lap, idx })).slice(-4).reverse();
  const cleanCount = c.laps.filter((lap) => lap.lap_number >= 1).length;
  // σ Timing cell (circuit only) — same selection rule as the desktop
  // DriverTab: the selected lap, else the most recent completed one.
  const timingIdx =
    c.isCircuit && c.laps.length > 0
      ? c.selectedLapIdx != null
        ? c.selectedLapIdx
        : c.laps.length - 1
      : null;
  const timingLap =
    timingIdx != null && c.laps[timingIdx]?.sigma_ms != null ? c.laps[timingIdx] : null;

  // Sections in usefulness order for the CURRENT state: a live car
  // leads with its ticking telemetry; an idle/replay car leads with
  // Sessions — the tappable footage list used to sit at the very
  // bottom of the scroll while a grid of dashes held the prime slot.
  const telemetrySection = (
      <MobileSection
        key="telemetry"
        title={c.replay ? 'Telemetry' : 'Live telemetry'}
        meta={
          recording ? (
            <span className="inline-flex items-center gap-1 text-status-online">
              <i className="tv-mobile-live-dot" />
              updating
            </span>
          ) : (
            <span>{c.replay ? 'replay' : 'idle'}</span>
          )
        }
      >
        <div className="grid grid-cols-2 gap-2">
          <MobileMetric
            label="Speed"
            value={speedKmh != null ? speedKmh.toFixed(1) : mutedDash}
            unit={speedKmh != null ? 'km/h' : undefined}
          />
          <MobileMetric
            label="Lag"
            // 'idle'/'no fix' are status words, not lag values — dash out.
            value={lag.severity === 'offline' ? mutedDash : lag.label}
            tone={SEVERITY_TEXT[lag.severity]}
          />
          <MobileMetric
            label="G peak"
            value={gForce ?? mutedDash}
            unit={gForce != null ? 'G' : undefined}
          />
          {/* Circuit: GPS-timing σ of the selected/most recent lap —
              replaces the old permanently-dead "Uplink —" cell (the
              viewer payload carries no uplink bitrate). */}
          {c.isCircuit && (
            <MobileMetric
              label="Timing"
              value={timingLap ? formatSigma(timingLap.sigma_ms!) : mutedDash}
              unit={timingLap ? `L${timingIdx! + 1}` : undefined}
              tone={timingLap ? sigmaToneClass(timingLap.confidence) : undefined}
              title={
                timingLap
                  ? `GPS timing 1σ uncertainty of L${timingIdx! + 1}`
                  : 'GPS timing uncertainty — appears once a lap is refined'
              }
            />
          )}
          <MobileMetric
            label="Battery"
            value={
              device.battery_level != null
                ? `${Math.round(device.battery_level * 100)}%`
                : mutedDash
            }
          />
          <MobileMetric
            label="Heading"
            value={device.heading_deg != null ? `${Math.round(device.heading_deg)}\u00b0` : mutedDash}
          />
        </div>
      </MobileSection>
  );

  const speedSection = c.trailTrack && c.trailTrack.length > 0 && (
        <MobileSection key="speed" title="Speed">
          {/* Playhead-synced speed chart. Section padding is ~2px so the
              chart sits nearly edge-to-edge and scales with the panel. */}
          <div className="h-28">
            <SpeedChart
              summary={{ track: c.trailTrack } as GpsSummary}
              currentUtcMs={c.playheadUtc}
              onSeek={c.handleSeek}
              unit="kmh"
              domainStartMs={c.videoStartMs}
              compact
              loadedEdgeUtc={
                c.trailCoverage && c.trailCoverage.length
                  ? Math.max(...c.trailCoverage.map(([, e]) => e))
                  : null
              }
            />
          </div>
          {/* Timeline scrubber — coverage fill + playhead + live edge. */}
          <div className="mt-2">
            <Scrubber
              startUtc={c.trailTrack[0][2]}
              endUtc={c.trailTrack[c.trailTrack.length - 1][2]}
              playheadUtc={c.playheadUtc}
              coverage={c.trailCoverage}
              onSeek={c.handleSeek}
            />
          </div>
        </MobileSection>
  );

  const progressSection = stagePct != null && c.stageLengthM != null && (
        <MobileSection
          key="progress"
          title="Stage progress"
          meta={`${stagePct.toFixed(0)}% / ${(c.stageLengthM / 1000).toFixed(2)} km`}
        >
          <div className="tv-mobile-progress">
            <div style={{ width: `${stagePct}%` }} />
          </div>
        </MobileSection>
  );

  const lapsSection = (
      <MobileSection
        key="laps"
        title={c.isCircuit ? 'Laps' : 'Stage events'}
        meta={c.isCircuit ? `${cleanCount}${recording ? ' + live' : ''}` : null}
      >
        {c.isCircuit ? (
          <div className="tv-mobile-laps">
            {recording && (
              <button
                type="button"
                onClick={c.handlePickLive}
                className={cn('tv-mobile-lap-chip', c.selectedLapIdx == null && 'is-active')}
              >
                <span>L{cleanCount + 1}</span>
                <b>live</b>
              </button>
            )}
            {recentLaps.map(({ lap, idx }) => (
              <button
                key={`${lap.session_id}-${lap.lap_number}`}
                type="button"
                onClick={() => c.handlePickLap(lap, idx)}
                className={cn('tv-mobile-lap-chip', c.selectedLapIdx === idx && 'is-active')}
              >
                <span>{lap.lap_number === 0 ? 'OUT' : `L${lap.lap_number}`}</span>
                <b>{formatLapTime(lap.duration_ms)}</b>
              </button>
            ))}
            {recentLaps.length === 0 && !recording && (
              <div className="py-1 font-mono text-[10px] text-fg-muted">no laps yet</div>
            )}
          </div>
        ) : c.stageEvents.length > 0 ? (
          <div className="tv-mobile-events">
            {c.stageEvents.slice(0, 4).map((event) => (
              <div key={event.id} className="tv-mobile-event-row">
                <span>{new Date(event.tsMs).toLocaleTimeString()}</span>
                <div>
                  <b>{event.text}</b>
                  <small>{event.sub}</small>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="py-1 font-mono text-[10px] text-fg-muted">no stage events yet</div>
        )}
      </MobileSection>
  );

  // Session surfacer — this car's recordings, stage-tagged; tap to
  // play (LIVE row returns to the live feed).
  const sessionsSection = (
      <SessionSelector
        key="sessions"
        rows={c.driverSessions}
        mountedSessionId={c.mountedSessionId}
        onPick={c.handlePickSession}
        className="border-0 p-0 pb-2"
      />
  );

  const ordered = recording
    ? [telemetrySection, speedSection, progressSection, lapsSection, sessionsSection]
    : [sessionsSection, speedSection, lapsSection, progressSection, telemetrySection];

  return (
    <div className="tv-mobile-watch-panel">
      {onClose && (
        <button
          type="button"
          className="tv-mobile-panel-close"
          onClick={onClose}
          aria-label="Close Watch"
        >
          <X className="size-4" />
        </button>
      )}
      {ordered}
    </div>
  );
};

const MobileCarsPanel: React.FC<{
  c: ViewerController;
  onSelectCar: (id: string) => void;
  onClose?: () => void;
}> = ({ c, onSelectCar, onClose }) => {
  const [query, setQuery] = useState('');
  const sorted = useMemo(() => {
    const liveScore = (device: EventLiveDevice) => (device.session_id != null ? 0 : 1);
    return [...c.filtered].sort(
      (a, b) => liveScore(a) - liveScore(b) || compareCarNumbers(a, b),
    );
  }, [c.filtered]);
  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return sorted;
    return sorted.filter(
      (device) =>
        displayCarNumber(device).toLowerCase().includes(q) ||
        driverName(device).toLowerCase().includes(q) ||
        (device.vehicle ?? '').toLowerCase().includes(q) ||
        device.device_id.toLowerCase().includes(q),
    );
  }, [query, sorted]);
  const followed = useMemo(
    () => visible.filter((device) => c.followedIds.has(device.device_id)),
    [c.followedIds, visible],
  );
  const rest = useMemo(
    () => visible.filter((device) => !c.followedIds.has(device.device_id)),
    [c.followedIds, visible],
  );

  // Stage filter (StageStrip): tag rows that have footage on the
  // selected stage; rows without dim slightly so availability scans.
  const stageActive = c.selectedStage != null && c.selectedStageLabel != null;
  const renderRow = (device: EventLiveDevice) => {
    const selected = c.selected?.device_id === device.device_id;
    const live = !c.replay && device.session_id != null;
    const stageHas = stageActive ? c.stageSessionByDevice.has(device.device_id) : null;
    return (
      <button
        key={device.device_id}
        type="button"
        onClick={() => onSelectCar(device.device_id)}
        className={cn(
          'tv-mobile-car-row',
          selected && 'is-selected',
          c.followedIds.has(device.device_id) && 'is-followed',
          stageHas === false && !live && 'opacity-55',
        )}
      >
        <span className={cn('tv-mobile-car-number', live && 'is-live')}>
          {displayCarNumber(device)}
        </span>
        <span className="min-w-0 flex-1">
          <b>{driverName(device)}</b>
          <small>{displayVehicle(device.vehicle) || <span>&mdash;</span>}</small>
        </span>
        {stageHas && (
          <span className="shrink-0 rounded-[3px] bg-black/40 px-1 py-px text-[8px] font-bold tracking-wide text-accent-cyan">
            {c.selectedStageLabel}
          </span>
        )}
        <LagChip lag={c.lagFor(device)} className="shrink-0" />
      </button>
    );
  };

  const group = (title: React.ReactNode, devices: EventLiveDevice[], meta?: React.ReactNode) => {
    if (devices.length === 0) return null;
    return (
      <div className="tv-mobile-car-group">
        <div className="tv-mobile-car-group-head">
          <span>{title}</span>
          {meta != null && <span>{meta}</span>}
        </div>
        {devices.map(renderRow)}
      </div>
    );
  };

  const restLive = c.replay ? 0 : rest.filter((device) => device.session_id != null).length;

  return (
    <div className="tv-mobile-cars-panel">
      <div className="tv-mobile-cars-meta">
        <span>
          {c.filtered.length} {c.filtered.length === 1 ? 'car' : 'cars'}
          {!c.replay && ` / ${c.liveCount} live`}
          {c.replay && ' / replay'}
        </span>
      </div>
      <div className="tv-mobile-search-row">
        <label className="tv-mobile-search">
          <Search className="size-3.5 shrink-0 text-fg-muted" />
          <input
            type="search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search number or name..."
            aria-label="Search cars by number, crew, vehicle or device id"
          />
        </label>
        {onClose && (
          <button
            type="button"
            className="tv-mobile-inline-close"
            onClick={onClose}
            aria-label="Close Cars"
          >
            <X className="size-4" />
          </button>
        )}
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto pb-2">
        {visible.length === 0 ? (
          <div className="tv-mobile-empty-panel">
            <Car className="size-5 text-accent" />
            <span>{query.trim() ? 'No cars match' : 'No armed phones yet'}</span>
          </div>
        ) : (
          <>
            {group(
              <span className="inline-flex items-center gap-1">
                <Star className="size-3 text-accent" />
                Following
              </span>,
              followed,
              `${followed.length}`,
            )}
            {group(
              c.replay ? 'All competitors' : 'All on stage',
              rest,
              !c.replay && restLive > 0 ? `${restLive} live` : undefined,
            )}
          </>
        )}
      </div>
    </div>
  );
};

const MobileMapPanel: React.FC<{
  c: ViewerController;
  resizeKey: string;
  onSelectCar: (id: string) => void;
  onClose?: () => void;
}> = ({ c, resizeKey, onSelectCar, onClose }) => (
  <div className="tv-mobile-map-panel">
    {onClose && (
      <button
        type="button"
        className="tv-mobile-map-close"
        onClick={onClose}
        aria-label="Close Map"
      >
        <X className="size-4" />
      </button>
    )}
    {c.modules ? (
      <MapCanvas
        modules={c.modules}
        center={c.center}
        event={c.event}
        devices={c.filtered}
        replay={c.replay}
        selectedId={c.selected?.device_id ?? null}
        onSelect={onSelectCar}
        lagFor={c.lagFor}
        followedIds={c.followedIds}
        trailTrack={c.trailTrack}
        trailCoverage={c.trailCoverage}
        playheadUtc={c.playheadUtc}
        onSeek={c.handleSeek}
        racelineCoords={c.racelineCoords}
        resizeKey={resizeKey}
        selectedStage={c.selectedStage}
        stageRoutes={c.stageRoutes}
        onSelectStage={c.setStage}
      />
    ) : (
      <div className="tv-mobile-empty-panel">
        <MapIcon className="size-5 text-accent" />
        <span>loading map...</span>
      </div>
    )}
  </div>
);

const MobileContextPanel: React.FC<{
  c: ViewerController;
  context: MobileContext;
  resizeKey: string;
  onSelectCar: (id: string) => void;
  onClose?: () => void;
}> = ({ c, context, resizeKey, onSelectCar, onClose }) => {
  if (context === 'watch') return <MobileWatchPanel c={c} device={c.selected} onClose={onClose} />;
  if (context === 'cars') {
    return <MobileCarsPanel c={c} onSelectCar={onSelectCar} onClose={onClose} />;
  }
  return (
    <MobileMapPanel
      c={c}
      resizeKey={resizeKey}
      onSelectCar={onSelectCar}
      onClose={onClose}
    />
  );
};

const LandscapeSheet: React.FC<{
  context: MobileContext;
  children: React.ReactNode;
}> = ({ context, children }) => (
    <aside
      className={cn(
        'tv-mobile-side-sheet',
        context === 'watch' && 'tv-mobile-watch-sheet',
        context === 'cars' && 'tv-mobile-cars-sheet',
        context === 'map' && 'tv-mobile-map-sheet',
      )}
    >
    <div className="tv-mobile-sheet-body min-h-0 flex-1 overflow-hidden">{children}</div>
  </aside>
);

export const MobileViewer: React.FC<{ c: ViewerController }> = ({ c }) => {
  const landscape = useMediaQuery(ORIENTATION_LANDSCAPE);
  const selectedId = c.selected?.device_id ?? null;
  const [activeContext, setActiveContext] = useState<MobileContext>(() =>
    deriveMobileContext(c.mode, c.mobilePanelOpen, selectedId != null),
  );
  const [sheetContext, setSheetContext] = useState<MobileContext | null>(null);
  const [telemetryCollapsed, setTelemetryCollapsed] = useState(false);
  // Mirrors the player's auto-hiding playbar (landscape) so sibling
  // overlays — telemetry HUD, driver label — move/collapse with it.
  const [chromeHidden, setChromeHidden] = useState(false);

  // Immersive landscape via the player's Maximise button: fullscreen the
  // WHOLE shell (not the player) so the nav island, telemetry and the
  // pull-out map all stay usable while the browser address bar is gone.
  const shellRef = useRef<HTMLDivElement>(null);
  const [shellFullscreen, setShellFullscreen] = useState(false);
  useEffect(() => {
    const onChange = () =>
      // Guard the null===null case: in portrait shellRef is unmounted, so
      // exiting fullscreen (e.g. rotating out of a maximised landscape)
      // fires with both sides null — must not read as "still maximised".
      setShellFullscreen(
        document.fullscreenElement != null &&
          document.fullscreenElement === shellRef.current,
      );
    document.addEventListener('fullscreenchange', onChange);
    return () => document.removeEventListener('fullscreenchange', onChange);
  }, []);
  const toggleShellFullscreen = useCallback(() => {
    const el = shellRef.current;
    if (!el) return;
    if (document.fullscreenElement) {
      void document.exitFullscreen().catch(() => undefined);
    } else {
      void el.requestFullscreen?.().catch(() => undefined);
    }
  }, []);

  useEffect(() => {
    setActiveContext((current) => {
      const derived = deriveMobileContext(c.mode, c.mobilePanelOpen, selectedId != null);
      if (current === 'watch' && selectedId == null) return derived;
      if (c.mobilePanelOpen) return 'cars';
      if (selectedId != null && (c.mode === 'detail' || c.mode === 'theater')) return 'watch';
      return current;
    });
  }, [c.mobilePanelOpen, c.mode, selectedId]);

  useEffect(() => {
    if (!landscape) setSheetContext(null);
  }, [landscape]);

  const activateContext = (context: MobileContext) => {
    if (context === 'watch' && selectedId == null) return;
    setActiveContext(context);
    if (context === 'watch') {
      c.setMobilePanelOpen(false);
      c.goDetail();
    } else {
      c.goBrowse();
      c.setMobilePanelOpen(context === 'cars');
    }
    setSheetContext(landscape ? context : null);
  };

  const selectCar = (id: string) => {
    c.selectCar(id, 'detail');
    c.setMobilePanelOpen(false);
    setActiveContext('watch');
    if (landscape) setSheetContext(null);
  };

  const closeLandscapeSheet = () => {
    setSheetContext(null);
    c.setMobilePanelOpen(false);
    if (selectedId != null) {
      setActiveContext('watch');
      c.goDetail();
    } else {
      setActiveContext('map');
      c.goBrowse();
    }
  };

  const navActive = sheetContext ?? activeContext;
  const orientation = landscape ? 'landscape' : 'portrait';

  if (landscape) {
    return (
      <div
        ref={shellRef}
        className={cn(
          'tv-mobile-viewer tv-mobile-landscape-shell',
          sheetContext && 'has-sheet',
          sheetContext === 'map' && 'has-map-sheet',
          chromeHidden && 'chrome-hidden',
        )}
      >
        <MobileVideoStage
          c={c}
          device={c.selected}
          landscape
          telemetryCollapsed={telemetryCollapsed}
          onTelemetryCollapsedChange={setTelemetryCollapsed}
          onControlsHiddenChange={setChromeHidden}
          onMaximize={toggleShellFullscreen}
          maximized={shellFullscreen}
        />
        <ViewerNavIsland
          active={navActive}
          orientation={orientation}
          hasCar={selectedId != null}
          onChange={activateContext}
        />
        {sheetContext && (
          <LandscapeSheet context={sheetContext}>
            <div className="flex h-full min-h-0 flex-col">
              {c.stageStripVisible && (
                <StageStrip
                  options={c.stageOptions}
                  selected={c.selectedStage}
                  onChange={c.setStage}
                  skin="panel"
                  className="shrink-0"
                />
              )}
              <div className="min-h-0 flex-1 overflow-hidden">
                <MobileContextPanel
                  c={c}
                  context={sheetContext}
                  resizeKey={`mobile-landscape-${sheetContext}-${selectedId ?? 'none'}`}
                  onSelectCar={selectCar}
                  onClose={closeLandscapeSheet}
                />
              </div>
            </div>
          </LandscapeSheet>
        )}
      </div>
    );
  }

  return (
    <div className="tv-mobile-viewer tv-mobile-portrait-shell">
      <MobileVideoStage
        c={c}
        device={c.selected}
        landscape={false}
        telemetryCollapsed={telemetryCollapsed}
        onTelemetryCollapsedChange={setTelemetryCollapsed}
      />
      {c.selected && (
        <MobileDriverHeader
          device={c.selected}
          replay={c.replay}
          recording={isRecording(c, c.selected)}
          followed={c.followedIds.has(c.selected.device_id)}
          lag={c.lagFor(c.selected)}
          onToggleFollow={c.toggleFollow}
        />
      )}
      {c.stageStripVisible && (
        <StageStrip
          options={c.stageOptions}
          selected={c.selectedStage}
          onChange={c.setStage}
          skin="panel"
          className="shrink-0"
        />
      )}
      <main className="tv-mobile-portrait-content">
        <MobileContextPanel
          c={c}
          context={activeContext}
          resizeKey={`mobile-portrait-${activeContext}-${selectedId ?? 'none'}`}
          onSelectCar={selectCar}
        />
      </main>
      <ViewerNavIsland
        active={activeContext}
        orientation={orientation}
        hasCar={selectedId != null}
        onChange={activateContext}
      />
    </div>
  );
};

export default MobileViewer;
